import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ShieldCheck, LogOut, ExternalLink } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { RedeemCode } from "@/components/RedeemCode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money } from "@/lib/api";

const perks = ["Unlimited clients & invoices", "One-tap invoice + receipt emails with PDF", "Expense tracking with receipt photos", "Monthly & year-end reports, profit summary", "Your logo on every invoice"];

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

export default function BillingPage() {
  const { user, logout, refreshBilling } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/billing/me").then((r) => { setMe(r.data); refreshBilling(r.data); }).catch(() => setMe(user?.billing || {}));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e), { duration: 9000 }); setBusy(""); }
  };

  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e), { duration: 9000 }); setBusy(""); }
  };

  const toggleCancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post(me.cancel_at_period_end ? "/billing/resume" : "/billing/cancel");
      setMe(data); refreshBilling(data);
      toast.success(me.cancel_at_period_end ? "Subscription resumed" : "Subscription will end at the period end");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const b = me || user?.billing || {};
  const locked = !b.entitled;
  const monthly = plans?.plans.find((p) => p.interval === "month");
  const yearly = plans?.plans.find((p) => p.interval === "year");
  const savings = monthly && yearly ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100) : 0;

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-card/90">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandMark size={36} />
            <div><div className="font-heading font-bold leading-tight">Basta10</div><div className="text-xs text-muted-foreground">{user?.business_name}</div></div>
          </div>
          <div className="flex gap-2">
            {!locked && <Button variant="outline" data-testid="billing-back-button" onClick={() => navigate("/clients")}>Back to app</Button>}
            <Button variant="ghost" data-testid="billing-logout-button" onClick={() => logout().then(() => navigate("/login"))}><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8 fade-up" data-testid="billing-page">
        <div className="max-w-xl">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="billing-headline">
            {b.status === "trial" ? "You're on your free trial" : b.status === "trialing" ? "Your card is on file" : b.status === "active" ? "Your subscription" : b.status === "past_due" ? "Payment problem" : b.status === "canceled" ? "Your subscription ended" : b.trial_expired ? "Your free trial has ended" : "Start your 14-day free trial"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base" data-testid="billing-status-text">
            {b.status === "trial" && `Free until ${fmt(b.trial_end)} — no card needed yet. Add one below whenever you like; billing starts at the end of the trial.`}
            {b.status === "trialing" && `Free until ${fmt(b.trial_end)} — then ${b.plan === "pro_yearly" ? "yearly" : "monthly"} billing starts automatically. Cancel anytime before.`}
            {b.status === "active" && `${b.plan === "pro_yearly" ? "Yearly" : "Monthly"} plan · ${b.cancel_at_period_end ? "ends" : "renews"} ${fmt(b.current_period_end)}.`}
            {b.status === "past_due" && "Your last payment failed. Update your card to keep access."}
            {b.status === "canceled" && "Pick a plan below to get back in — all your clients and invoices are still here."}
            {(b.status === "none" || !b.status) && (b.trial_expired ? "Pick a plan to keep going — all your clients and invoices are safe and waiting." : "No card charged for 14 days. Pick monthly or yearly, cancel anytime.")}
            {b.comp_until && <span className="block mt-1 text-emerald-600 font-medium" data-testid="billing-comp-note">Complimentary access until {fmt(b.comp_until)}.</span>}
          </p>
        </div>

        {locked && (
          <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 p-4 text-sm" data-testid="billing-locked-banner">
            Your app is locked until a plan is active. Your data is safe and will be right where you left it.
          </div>
        )}

        {(b.status === "trialing" || b.status === "active" || b.status === "past_due") && b.has_customer && (
          <div className="surface p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Manage card, invoices and plan in the secure billing portal.</div>
            <div className="flex gap-2">
              <Button data-testid="billing-portal-button" variant="outline" onClick={portal} disabled={!!busy}><ExternalLink className="h-4 w-4 mr-2" />Billing portal</Button>
              <Button data-testid="billing-cancel-button" variant={b.cancel_at_period_end ? "default" : "ghost"} onClick={toggleCancel} disabled={!!busy} className={b.cancel_at_period_end ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "text-red-600"}>
                {b.cancel_at_period_end ? "Resume subscription" : "Cancel"}
              </Button>
            </div>
          </div>
        )}

        {!plans ? <p className="text-sm text-muted-foreground">Loading plans...</p> : (b.status !== "active" && b.status !== "trialing") && (
          <div className="grid sm:grid-cols-2 gap-4">
            {[monthly, yearly].filter(Boolean).map((p) => (
              <div key={p.lookup_key} data-testid={`plan-card-${p.interval}`} className={`surface p-6 flex flex-col gap-4 relative ${p.interval === "year" ? "border-emerald-500 ring-1 ring-emerald-500" : ""}`}>
                {p.interval === "year" && savings > 0 && <span className="absolute -top-3 left-5 bg-amber-500 text-slate-950 text-xs font-bold px-2.5 py-1 rounded-full">Save {savings}%</span>}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{p.label}</div>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="font-mono font-bold text-4xl">{money(p.amount)}</span>
                    <span className="text-muted-foreground text-sm">/ {p.interval}</span>
                  </div>
                  {p.interval === "year" && monthly && <div className="text-xs text-muted-foreground mt-1">≈ {money(p.amount / 12)} per month</div>}
                </div>
                <ul className="space-y-2 text-sm flex-1">
                  {perks.map((x) => <li key={x} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{x}</li>)}
                </ul>
                <Button data-testid={`plan-subscribe-${p.interval}`} onClick={() => subscribe(p.lookup_key)} disabled={!!busy}
                  className={`h-12 font-bold ${p.interval === "year" ? "zap-btn rounded-lg" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  {busy === p.lookup_key ? "Redirecting..." : b.status === "trial" ? `Add card — ${p.label.toLowerCase()}` : (b.status === "none" || !b.status) && !b.trial_expired ? `Start free trial — ${p.label.toLowerCase()}` : `Subscribe ${p.label.toLowerCase()}`}
                </Button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">Payments are processed securely by Stripe. Test card: 4242 4242 4242 4242, any future date, any CVC.</p>
        <RedeemCode onRedeemed={(data) => { setMe(data); refreshBilling(data); }} />
      </main>
    </div>
  );
}
